import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, UploadCloud } from "lucide-react";

export default function CandidateForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    current_role: "",
    years_experience: 3,
    preferred_country: "germany",
    skills: "",
    cv_filename: "",
    cv_text: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    update("cv_filename", file.name);
    // Read first ~50KB as plain text preview (works for .txt; for PDF we
    // just store filename — backend stores text if available).
    if (file.type === "text/plain" || file.name.endsWith(".txt")) {
      const text = await file.text();
      update("cv_text", text.slice(0, 50000));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/candidate", {
        ...form,
        years_experience: Number(form.years_experience) || 0,
      });
      setDone(true);
      toast.success("CV received — we'll match you with roles within 48h.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="candidate-form-success"
        className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-emerald-900"
      >
        <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        <div className="font-display font-bold text-xl mt-3">
          You're in our talent pool, {form.full_name.split(" ")[0] || "friend"}.
        </div>
        <p className="mt-2 text-emerald-800/80">
          We'll match your profile with open roles in{" "}
          <b>{form.preferred_country}</b> and reach out to {form.email} within
          48 hours.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid="candidate-form"
      onSubmit={submit}
      className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5"
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name">
          <Input
            data-testid="cand-name"
            required
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
          />
        </Field>
        <Field label="Email">
          <Input
            data-testid="cand-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>
        <Field label="Phone (optional)">
          <Input
            data-testid="cand-phone"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </Field>
        <Field label="Current role">
          <Input
            data-testid="cand-role"
            placeholder="e.g. Senior Java Developer"
            value={form.current_role}
            onChange={(e) => update("current_role", e.target.value)}
          />
        </Field>
        <Field label="Years experience">
          <Input
            data-testid="cand-years"
            type="number"
            min={0}
            value={form.years_experience}
            onChange={(e) => update("years_experience", e.target.value)}
          />
        </Field>
        <Field label="Preferred country">
          <Select
            value={form.preferred_country}
            onValueChange={(v) => update("preferred_country", v)}
          >
            <SelectTrigger data-testid="cand-country">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="germany">Germany</SelectItem>
              <SelectItem value="netherlands">Netherlands</SelectItem>
              <SelectItem value="romania">Romania</SelectItem>
              <SelectItem value="poland">Poland</SelectItem>
              <SelectItem value="bulgaria">Bulgaria</SelectItem>
              <SelectItem value="remote-europe">Remote Europe</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Key skills">
        <Textarea
          data-testid="cand-skills"
          rows={2}
          placeholder="React, TypeScript, AWS, microservices…"
          value={form.skills}
          onChange={(e) => update("skills", e.target.value)}
        />
      </Field>
      <Field label="CV upload">
        <label
          data-testid="cand-cv-label"
          className="flex items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 cursor-pointer hover:bg-slate-100 transition"
        >
          <UploadCloud className="w-5 h-5 text-slate-500" />
          <div className="text-sm text-slate-600">
            {form.cv_filename
              ? `Selected: ${form.cv_filename}`
              : "Click to upload your CV (PDF, DOCX, TXT)"}
          </div>
          <input
            data-testid="cand-cv-input"
            type="file"
            accept=".pdf,.docx,.doc,.txt"
            className="hidden"
            onChange={onFile}
          />
        </label>
      </Field>
      <Button
        data-testid="cand-submit"
        type="submit"
        disabled={loading}
        className="w-full bg-teal-700 hover:bg-teal-800 text-white h-11"
      >
        {loading ? "Submitting…" : "Apply Now"}
      </Button>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-slate-500 font-semibold">
        {label}
      </Label>
      {children}
    </div>
  );
}
